import React from 'react';
import { ProjectData } from '../services/api';
import { formatNumber } from '../utils/formatUtils';

interface CircularInflowOutflowProps {
  projects: ProjectData[];
  selectedYear: number;
}

const CircularInflowOutflow: React.FC<CircularInflowOutflowProps> = ({
  projects,
  selectedYear,
}) => {
  // Filter projects by selected year
  const yearProjects = projects.filter(
    project =>
      project?.createdAt &&
      new Date(project.createdAt).getFullYear() === selectedYear
  );

  // Aggregate circularity data from projects in selected year
  const aggregateCircularityData = (filteredProjects: ProjectData[]) => {
    const totals = {
      totalMaterialKg: 0,
      inflow: {
        recycledContentPreConsumerKg: 0,
        recycledContentPostConsumerKg: 0,
        reusedMaterialsKg: 0,
        biomassMaterialsKg: 0,
      },
      outflow: {
        recyclableMaterialsKg: 0,
        reusableMaterialsKg: 0,
        demolitionSystemsKg: 0,
        installationSystemsKg: 0,
      },
    };

    // First, sum up all the actual material amounts across filtered projects
    filteredProjects.forEach(project => {
      const snapshot = project.latestSnapshot?.snapshotData;
      if (!snapshot?.circularity) return;

      const { circularity } = snapshot;

      totals.totalMaterialKg += circularity.totalMaterialKg;
      totals.inflow.recycledContentPreConsumerKg +=
        circularity.inflow.recycledContentPreConsumerKg;
      totals.inflow.recycledContentPostConsumerKg +=
        circularity.inflow.recycledContentPostConsumerKg;
      totals.inflow.reusedMaterialsKg += circularity.inflow.reusedMaterialsKg;
      totals.inflow.biomassMaterialsKg += circularity.inflow.biomassMaterialsKg;

      totals.outflow.recyclableMaterialsKg +=
        circularity.outflow.recyclableMaterialsKg;
      totals.outflow.reusableMaterialsKg +=
        circularity.outflow.reusableMaterialsKg;
      totals.outflow.demolitionSystemsKg +=
        circularity.outflow.demolitionSystemsKg;
      totals.outflow.installationSystemsKg +=
        circularity.outflow.installationSystemsKg;
    });

    // Then calculate percentages based on total amounts
    const calculatePercentage = (amount: number, total: number) => {
      return total > 0 ? Math.round((amount / total) * 100) : 0;
    };

    return {
      inflow: {
        recycledContent: calculatePercentage(
          totals.inflow.recycledContentPreConsumerKg +
            totals.inflow.recycledContentPostConsumerKg,
          totals.totalMaterialKg
        ),
        reusedMaterials: calculatePercentage(
          totals.inflow.reusedMaterialsKg,
          totals.totalMaterialKg
        ),
        biomassMaterial: calculatePercentage(
          totals.inflow.biomassMaterialsKg,
          totals.totalMaterialKg
        ),
      },
      outflow: {
        recyclableMaterials: calculatePercentage(
          totals.outflow.recyclableMaterialsKg,
          totals.totalMaterialKg
        ),
        reusableMaterials: calculatePercentage(
          totals.outflow.reusableMaterialsKg,
          totals.totalMaterialKg
        ),
        demolition: calculatePercentage(
          totals.outflow.demolitionSystemsKg,
          totals.totalMaterialKg
        ),
        installation: calculatePercentage(
          totals.outflow.installationSystemsKg,
          totals.totalMaterialKg
        ),
      },
    };
  };

  const circularMetrics = aggregateCircularityData(yearProjects);

  return (
    <div className="max-w-[1440px] mx-auto">
      <div className="grid grid-cols-2 gap-6">
        {/* Circular Inflow */}
        <div className="bg-white rounded-lg border border-[#e9eaeb] shadow-sm">
          <div className="border-b border-[#e9e9e8] px-6 py-4">
            <h2 className="text-[16px] font-bold text-[#696969]">
              Circular Inflow - Production stage (A1 - A3)
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Recycled Content */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Recycled Content
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.inflow.recycledContent)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.inflow.recycledContent,
                      100
                    )}%`,
                  }}
                />
              </div>
              <div className="flex gap-6 text-[12px] text-[#636b63]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#2a654e]" />
                  <span>Pre-consumer</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#82ab9b]" />
                  <span>Post-consumer</span>
                </div>
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Recycled content includes pre-consumer and post-consumer
                recycled materials.
              </p>
            </div>

            {/* Reused Materials */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Reused materials
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.inflow.reusedMaterials)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.inflow.reusedMaterials,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Reused materials are components or materials that are used again
                in their original form.
              </p>
            </div>

            {/* Biomass Material */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Biomass Material
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.inflow.biomassMaterial)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.inflow.biomassMaterial,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Biomass materials are renewable organic materials from plants
                and animals.
              </p>
            </div>
          </div>
        </div>

        {/* Circular Outflow */}
        <div className="bg-white rounded-lg border border-[#e9eaeb] shadow-sm">
          <div className="border-b border-[#e9e9e8] px-6 py-4">
            <h2 className="text-[16px] font-bold text-[#696969]">
              Circular Outflow - End of life stage (C1 - C4)
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Total recyclable materials */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Total recyclable materials
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.outflow.recyclableMaterials)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.outflow.recyclableMaterials,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Materials that can be broken down and processed into new raw
                materials for manufacturing new products. Unlike reusable
                materials, these require processing before being reintroduced
                into the supply chain.
              </p>
            </div>

            {/* Total reusable materials */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Total reusable materials
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.outflow.reusableMaterials)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.outflow.reusableMaterials,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Materials that can be salvaged and used again in their original
                form with little to no processing. These materials retain their
                structural integrity and function after deconstruction or
                renovation.
              </p>
            </div>

            {/* Demolition */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Demolition
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.outflow.demolition)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.outflow.demolition,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Demolition recycling systems recover, sort, and process
                materials from deconstructed buildings for reuse, reducing waste
                and supporting circular construction.
              </p>
            </div>

            {/* Installation */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[14px] font-medium text-[#081e0a]">
                  Installation
                </span>
                <span className="text-[14px] font-medium text-[#081e0a]">
                  {formatNumber(circularMetrics.outflow.installation)}%
                </span>
              </div>
              <div className="bg-[#f3f0eb] rounded-full h-3">
                <div
                  className="bg-[#2a654e] h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      circularMetrics.outflow.installation,
                      100
                    )}%`,
                  }}
                />
              </div>
              <p className="text-[14px] text-[#636b63] leading-6">
                Installation recycling systems capture and process waste from
                new construction or renovations, ensuring materials like
                packaging, offcuts, and surplus are recycled or reused.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircularInflowOutflow;
